// @flow
import React, { Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import DealerModel from 'Stores/DealerModel';
import Icon from 'Components/Icon';
import Button from 'Components/Button';
import Link from 'Components/Link';
import LocationSwitch from './LocationSwitch';
import Flyout from './Flyout';
import stylesFlyout from './Flyout/styles.scss';
import styles from './styles.scss';
import lsStyles from './LocationSwitch/styles.scss';
import classNames from 'classnames/bind';
import MainEntry from './mainentry.js';
import LeadNav from './LeadNav';
import { emailTracking, telephoneTracking, whatsappTracking } from 'Utils/trackingAttributes';
import { flyoutTeaserTracking } from 'Utils/trackingAttributes';
import { withUrlParams } from 'Utils/withUrlParams';
import type { NavigationItem } from 'Stores/NavigationItem';
import type { RouterHistory } from 'react-router-dom';
import Logo from 'Components/Logo';

declare var PUBLIC_BASE_URL: string;

const cx = classNames.bind({ ...styles, ...lsStyles });
const cxFlyout = classNames.bind({ ...stylesFlyout });

export const TOGGLE_LOCATION_SWITCH = 'toggleLocationSwitch';
const LOCATION_SWITCH_ID = `location-switch`;

const matchMedia = window.matchMedia('(min-width: 768px)');

type Props = {
  dealer: DealerModel,
  menu: Array<Object>,
  teaser: Object,
  history: RouterHistory,
  type: string,
  location: Object,
  onOpenContact: () => void,
  onOpenConfigurator: () => void,
  showMapClicked: boolean,
  setShowMapClicked: () => void,
  error?: ?boolean,
};

type State = {
  locationSwitchOpen: boolean,
  mapInit: boolean,
  flyoutOpen: boolean,
  mobileFlyoutOpen: boolean,
  flyoutSubOpen: boolean,
  flyouts: Array<Object>,
  resetActiveState: boolean,
};

class Header extends React.Component<Props, State> {
  static defaultProps = {
    dealer: new DealerModel(),
    teaser: {},
    menu: [],
  };

  constructor(props) {
    super(props);

    const entries = this.props.menu.filter(
      (entry: NavigationItem) => !entry.footer && !entry.hideInMenu
    );
    const flyouts = entries.map((entry: NavigationItem) => {
      return {
        id: entry.id,
        label: entry.label,
        hideInMenu: entry.hideInMenu,
        subentries: entry.subentries,
        // using href here is workaround to handle 1lvl entries in HI without subentries
        href: entry.href,
        open: false,
        active: this.props.location.pathname.indexOf(entry.path) !== -1,
        transitionDelay: false,
      };
    });

    this.state = {
      locationSwitchOpen: false,
      mapInit: false,
      flyoutOpen: false,
      mobileFlyoutOpen: false,
      mobileFlyoutHide: true,
      flyoutSubOpen: false,
      flyouts,
      resetActiveState: false,
      isMobileNavigationNeeded: !matchMedia.matches || this.props.forceMobileMenu,
    };
  }

  // #region component hook
  shouldComponentUpdate = (nextProps: Props, nextState: State) => {
    const { flyoutOpen, locationSwitchOpen, mobileFlyoutOpen, resetActiveState, showMapClicked } =
      this.state;
    // Re-render menu only when a flyout toggles
    return (
      flyoutOpen !== nextState.flyoutOpen ||
      locationSwitchOpen !== nextState.locationSwitchOpen ||
      mobileFlyoutOpen !== nextState.mobileFlyoutOpen ||
      resetActiveState !== nextState.resetActiveState ||
      showMapClicked !== nextProps.showMapClicked
    );
  };

  componentDidMount() {
    const { resetActiveState, flyouts } = this.state;
    const { history } = this.props;

    matchMedia.addEventListener('change', this.onMatchMedia);

    this.openLocationOnDeepLink();

    // https://github.com/remix-run/history/blob/v4/docs/GettingStarted.md#listening
    history.listen((_location, _action) => {
      this.setState({
        flyouts: flyouts.map((flyout) => ({ ...flyout, active: false })),
        resetActiveState: !resetActiveState,
      });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { flyouts, mobileFlyoutOpen, flyoutSubOpen } = this.state;
    const { urlParams, forceMobileMenu } = this.props;

    if (prevProps.urlParams.showlocations !== urlParams.showlocations) {
      this.openLocationOnDeepLink();
    }

    if (prevProps.forceMobileMenu !== forceMobileMenu) {
      const flyoutsMapped = flyouts.map((item) => ({ ...item, open: false }));
      this.setState({
        flyoutSubOpen: false,
        flyoutOpen: false,
        flyouts: flyoutsMapped,
      });
      this.onMatchMedia();
    }

    if (forceMobileMenu) {
      document.body.classList.add(cx('hide-scrollbar'));
    } else {
      document.body.classList.remove(cx('hide-scrollbar'));
    }

    if (prevState.mobileFlyoutOpen !== mobileFlyoutOpen) {
      if (mobileFlyoutOpen) {
        this.setState({ mobileFlyoutHide: false });
        document.body.classList.add(cx('mobile-burger--open'));
      } else {
        const mobileBurger = document.querySelector('#header-mobile-burger');
        mobileBurger.focus();

        setTimeout(() => {
          this.setState({ mobileFlyoutHide: true });
        }, 300);
        document.body.classList.remove(cx('mobile-burger--open'));
      }
    }

    if (prevState.flyoutSubOpen !== flyoutSubOpen) {
      if (flyoutSubOpen) {
        const idOfOpenedFlyoutSub = flyouts.find((item) => item.open === true)?.id;
        const buttonOfFlyoutSub = document.querySelector(
          `.${cxFlyout('flyout-head')}[aria-controls="header-lvl-2-${idOfOpenedFlyoutSub}"]`
        );

        if (buttonOfFlyoutSub) {
          setTimeout(() => {
            buttonOfFlyoutSub.focus();
          }, 300);
        }
      } else {
        const idOfClosedFlyoutSub = flyouts.find((item) => item.transitionDelay === false).id;
        const buttonOfFlyoutSub = document.querySelector(`#header-lvl-1-${idOfClosedFlyoutSub}`);
        buttonOfFlyoutSub.focus();
      }
    }
  }

  componentWillUnmount() {
    matchMedia.removeEventListener('change', this.onMatchMedia);
  }

  openLocationOnDeepLink = () => {
    const { isMobileNavigationNeeded, mobileFlyoutOpen } = this.state;
    const { urlParams } = this.props;

    if (urlParams.showlocations) {
      if (isMobileNavigationNeeded) {
        this.setState({
          locationSwitchOpen: true,
          mobileFlyoutOpen: true,
          flyoutSubOpen: false,
          flyoutOpen: false,
        });

        if (!mobileFlyoutOpen) {
          document.body.classList.add(cx('mobile-burger--open'));
        } else {
          document.body.classList.remove(cx('mobile-burger--open'));
        }

        setTimeout(() => {
          const card = document.querySelector(`#${LOCATION_SWITCH_ID}`);
          if (card) card.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
        }, 1000);
      } else {
        this.setState({
          locationSwitchOpen: true,
          flyoutSubOpen: false,
          flyoutOpen: false,
        });
      }
    }
  };
  // #endregion

  toggleLocationSwitch = (ariaControlsId) => {
    const { locationSwitchOpen } = this.state;

    const newLocationSwitchOpen = !locationSwitchOpen;
    this.setState({
      locationSwitchOpen: newLocationSwitchOpen,
      mapInit: true,
    });

    if (newLocationSwitchOpen) {
      window.setTimeout(() => {
        document.querySelector('#locationcard-mainbranch')?.focus();
      }, 250);
    } else if (!newLocationSwitchOpen) {
      document.querySelector(`[aria-controls="${ariaControlsId}"]`)?.focus();
    }

    this.updateLocationSwitchScrollPosition();
  };

  updateLocationSwitchScrollPosition = () => {
    window.scrollTo(0, 0);

    window.setTimeout(() => {
      document.querySelector('.' + cx('header__wrapper')).scrollTop = document.querySelector(
        '.' + cx('header__location-switch')
      ).offsetHeight;
    }, 250);
  };

  openLocationSwitch = () => {
    this.setState({
      locationSwitchOpen: true,
      mapInit: true,
    });

    this.updateLocationSwitchScrollPosition();
  };

  handleFlyout = (entry: Object) => {
    const { flyouts, flyoutOpen, flyoutSubOpen, mobileFlyoutOpen } = this.state;

    if (entry.id) {
      // id = -666 => deny
      if (entry.id !== -666) {
        const indexToOpen = flyouts.findIndex((flyout) => flyout.id === entry.id);
        const indexOpen = flyouts.findIndex((flyout) => flyout.open === true);
        const prevstate = flyouts[indexToOpen].open;

        flyouts.forEach((flyout, _index) => {
          flyout.open = false;
          flyout.transitionDelay = true;
        });

        if (flyoutSubOpen && flyouts[indexOpen]) {
          flyouts[indexOpen].transitionDelay = false;
        } else {
          flyouts[indexToOpen].transitionDelay = false;
        }

        if (!entry.path) {
          flyouts[indexToOpen].open = !prevstate;
        }

        this.setState({
          flyoutOpen: !flyoutOpen,
          flyouts,
        });
      } else {
        this.setState({
          mobileFlyoutOpen: false,
        });
      }
    } else {
      this.setState({
        mobileFlyoutOpen: !mobileFlyoutOpen,
      });
    }

    document.querySelector('.' + cx('header__wrapper')).scrollTop = 0;
  };

  setEntryActiveState = (entry) => {
    const { flyouts } = this.state;

    const indexToActivate = flyouts.findIndex((flyout) => flyout.id === entry.id);

    flyouts.map((flyout) => ({ ...flyout, active: false }));

    flyouts[indexToActivate].active = true;

    this.setState({
      flyouts,
    });
  };

  // #region event handler
  onMainEntryClick = (entry) => {
    if (!entry.subentries || entry.subentries.length === 0) {
      // using href here is workaround to handle 1lvl in HI entries without subentries
      this.props.history.push(entry.href);
      this.setEntryActiveState(entry);
      return;
    }

    if ((this.state.flyoutSubOpen && entry.open) || entry.path) {
      this.setState({
        flyoutSubOpen: false,
      });
    } else {
      this.setState({
        flyoutSubOpen: true,
      });
    }
    this.handleFlyout(entry);
  };

  onMainEntryKeyDown = (event, entry) => {
    const isKeyTab = event.key === 'Tab';
    const isKeyShift = event.shiftKey;

    if (isKeyTab && !isKeyShift && entry.open) {
      event.preventDefault();

      const firstElementInFlyout = document.querySelector(`#header-lvl-2-${entry.id} a`);
      firstElementInFlyout.focus();
    }
  };

  onLogoClick = () => {
    if (this.state.locationSwitchOpen) {
      this.toggleLocationSwitch(LOCATION_SWITCH_ID);
    }
  };

  onLinkClick = (mainentry) => {
    const { mobileFlyoutOpen } = this.state;

    if (!mobileFlyoutOpen) {
      this.setEntryActiveState(mainentry);
      this.onMainEntryClick(mainentry);
    } else {
      this.handleFlyout({});
    }
  };

  onTeaserButtonClick = (entry) => {
    this.onMainEntryClick(entry);

    if (this.state.locationSwitchOpen) {
      this.updateLocationSwitchScrollPosition();
      return;
    }

    this.openLocationSwitch();
  };

  onKeyDown = (event) => {
    const {
      flyoutOpen,
      flyouts,
      flyoutSubOpen,
      isMobileNavigationNeeded,
      locationSwitchOpen,
      mobileFlyoutOpen,
    } = this.state;

    const focusableComponents = ['a', 'button'];

    const isKeyEsc = event.key === 'Escape';
    const isKeyTab = event.key === 'Tab';
    const isKeyShift = event.shiftKey;

    if (isKeyEsc) {
      if (
        (locationSwitchOpen ||
          flyoutOpen ||
          mobileFlyoutOpen ||
          flyouts.some((item) => item.open === true)) &&
        (!flyoutSubOpen || !isMobileNavigationNeeded) // flyoutSub has its own esc behaviour
      ) {
        event.preventDefault();
        const flyouts = flyouts.map((item) => ({ ...item, open: false }));
        this.setState({
          locationSwitchOpen: false,
          flyoutSubOpen: false,
          flyoutOpen: false,
          mobileFlyoutOpen: false,
          flyouts,
        });
      }
    } else if (isMobileNavigationNeeded && (isKeyTab || isKeyShift) && mobileFlyoutOpen) {
      // tab through all focusable elements in mobile/mini navigation via tab

      const focusableElementsSelector = focusableComponents
        .map((item) => `#header ${item}`)
        .map((item) => `${item}:not([disabled]):not([hidden]):not([tabindex="-1"])`);
      const focusableElements = [...document.querySelectorAll(focusableElementsSelector)]
        .filter((item) => !item.closest('[hidden]') && item.checkVisibility())
        .sort((itemA, itemB) => {
          const tabIndexItemA = parseInt(itemA.getAttribute('tabindex'));
          const tabIndexItemB = parseInt(itemB.getAttribute('tabindex'));

          return tabIndexItemA > tabIndexItemB;
        });
      const focusableElementFirst = focusableElements[0];
      const focusableElementLast = focusableElements[focusableElements.length - 1];

      const reachedStartOfFocusableElementList =
        isKeyShift && isKeyTab && event.target === focusableElementFirst;
      const reachedEndOfFocusableElementList =
        !isKeyShift && isKeyTab && event.target === focusableElementLast;

      if (reachedStartOfFocusableElementList) {
        event.preventDefault();
        focusableElementLast.focus();
      } else if (reachedEndOfFocusableElementList) {
        event.preventDefault();
        focusableElementFirst.focus();
      }
    }
    // focus a main navigation item after (shift) tab on first/last item in sub navigation
    else if (!isMobileNavigationNeeded && isKeyTab && flyouts) {
      const focusWithinFlyout = event.target.closest(`.${cxFlyout('header__flyout')}`);

      if (!focusWithinFlyout) {
        return false;
      }

      const allFocusableElements = [
        ...focusWithinFlyout.querySelectorAll(focusableComponents.join(',')),
      ].filter((item) => item.checkVisibility());

      const isFirstFocusableElement =
        allFocusableElements[0] === event.target && isKeyTab && isKeyShift;
      const isLastFocusableElement =
        allFocusableElements[allFocusableElements.length - 1] === event.target &&
        isKeyTab &&
        !isKeyShift;

      if (isFirstFocusableElement || isLastFocusableElement) {
        const navigationLabelledby = focusWithinFlyout.getAttribute('aria-labelledby');
        const navigationMainItem = document.querySelector(`#${navigationLabelledby}`);
        const nextMainNavigationItem = navigationMainItem.parentNode.nextSibling;

        if (isFirstFocusableElement) {
          event.preventDefault();
          navigationMainItem.focus();
        } else if (isLastFocusableElement && nextMainNavigationItem) {
          event.preventDefault();
          const button = nextMainNavigationItem.querySelector('button');
          button.focus();
        }
      }
    }
  };

  onMatchMedia = () => {
    const { forceMobileMenu } = this.props;

    this.setState({
      isMobileNavigationNeeded: !matchMedia.matches || forceMobileMenu,
    });
  };
  // #endregion

  // #region render
  renderTeaser = (entry) => {
    const { forceMobileMenu, teaser: teasers } = this.props;

    let teaser = teasers[entry.id];
    const dfmm = { 'data-force-mobile-menu': forceMobileMenu };
    const headlineId = `header-headline-${entry.id}`;

    if (teaser) {
      const onClick =
        teaser.onClick === TOGGLE_LOCATION_SWITCH
          ? () => this.onTeaserButtonClick(entry)
          : teaser.onClick;

      return (
        <div className={styles.teaser} {...dfmm}>
          <p id={headlineId} className={styles.headline} {...dfmm}>
            {teaser.text}
          </p>
          <div className={styles.button} {...(teaser.link ? dfmm : {})}>
            <Button
              href={teaser.link ? teaser.link.href : undefined}
              target={teaser.link ? teaser.link.target : undefined}
              label={teaser.label}
              ariaLabelledby={headlineId}
              ariaLabel={teaser.link ? undefined : `${teaser.label} - öffnet in einem Modal`}
              onClick={onClick}
              additionalProps={flyoutTeaserTracking(
                teaser.customTrackingLabel || teaser.label.toLowerCase()
              )}
            />
          </div>
        </div>
      );
    }
    return <div />;
  };

  renderEkiLogo = (className, dealerName) => {
    const { dealer } = this.props;

    if (dealer.hasLogo && dealer.id) {
      return (
        <img
          src={`${PUBLIC_BASE_URL}/img/dealer-logos/${dealer.id}.webp`}
          alt={`Logo von ${dealerName}`}
          className={cx(`${className}__image`)}
        />
      );
    }

    return <span className={cx(`${className}__text`)}>{dealerName}</span>;
  };

  renderHeaderMeta = (isMobileNavigationNeeded, dfmm, dealer, type, locationSwitchOpen) => {
    const itemDealerWebsite = dealer.settings.socialMedia &&
      dealer.settings.socialMedia.dealerlink &&
      dealer.settings.socialMedia.dealerlink !== '' && (
        <li className={cx('meta-nav__item', 'meta-nav__item--dealer-website')} {...dfmm}>
          <Link
            to={dealer.settings.socialMedia.dealerlink}
            target="_blank"
            rel="noreferrer"
            label={
              dealer.settings.socialMedia.dealerlinkTitle !== ''
                ? dealer.settings.socialMedia.dealerlinkTitle
                : 'zur Händlerwebseite'
            }
          />
        </li>
      );

    const hasMultipleBranches = dealer.branches && dealer.branches.length > 1;
    const locationSwitchButtonLabel = `Standort${hasMultipleBranches ? 'e' : ''}`;
    const locationSwitchButtonAriaLabel =
      locationSwitchButtonLabel +
      ' ' +
      (locationSwitchOpen ? 'verbergen' : 'anzeigen') +
      '. Erfahre die Adresse, Kontaktdaten und Öffnungszeiten unseres Standorts';

    const itemLocationSwitch = (
      <li
        className={cx('meta-nav__item', 'meta-nav__item--ls', {
          'meta-nav__item--active': locationSwitchOpen,
        })}
        data-e2e="ourlocations"
        {...dfmm}
      >
        <Button
          className={cx('meta-nav__item-button')}
          label={locationSwitchButtonLabel}
          onClick={() => this.toggleLocationSwitch(LOCATION_SWITCH_ID)}
          header={true}
          ariaExpanded={locationSwitchOpen}
          ariaControls={LOCATION_SWITCH_ID}
          ariaLabel={locationSwitchButtonAriaLabel}
          textEnd={<Icon name="caret-down" className={styles.icon} inline={false} />}
          additionalProps={dfmm}
        />
      </li>
    );
    const itemPhone = (
      <li className={cx('meta-nav__item', 'meta-nav__item--phone')} {...dfmm}>
        <Link
          to={`tel:${dealer.mainContact.phone}`}
          {...telephoneTracking('Navigation Bar')}
          label={dealer.mainContact.phone}
        />
      </li>
    );
    const whatsappUrl =
      dealer.settings && dealer.settings.whatsappWidget && dealer.settings.whatsappWidget.isActive
        ? dealer.settings.whatsappWidget.whatsappUrl
        : undefined;
    const itemEmailAndWhatsApp = (
      <>
        <li className={cx('meta-nav__item', 'meta-nav__item--email')} {...dfmm}>
          <Link
            to={`mailto:${dealer.mainContact.email}`}
            {...emailTracking('Navigation Bar')}
            label="E-Mail"
          />
        </li>
        {whatsappUrl && (
          <li className={cx('meta-nav__item', 'meta-nav__item--email')} {...dfmm}>
            <Link
              to={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              {...whatsappTracking('Navigation Bar')}
              label="WHATSAPP"
            />
          </li>
        )}
      </>
    );

    return (
      <div className={cx('header__meta')}>
        <div className={cx('gr-container')} {...dfmm}>
          <div className={cx('meta-logo')} {...dfmm}>
            <Link
              to={type === 'hi' ? '/admin' : '/'}
              className={cx('meta-logo__link')}
              ariaLabel={
                type === 'hi' ? 'Zur Portal-Anmeldung' : `Zur Startseite von ${dealer.name}`
              }
              onClick={this.onLogoClick}
            >
              <Logo className={cx('meta-logo__image')} width={50} height={44} {...dfmm} />
              {type === 'hi' && 'Händlerinterface'}
            </Link>
            {dealer.settings.servicePartnerOnly &&
              !(
                type === 'eki' &&
                dealer.hasFMMData &&
                dealer.settings.socialMedia &&
                dealer.settings.socialMedia.dealerlink &&
                dealer.settings.socialMedia.dealerlink !== ''
              ) && (
                <span className={cx('service')} aria-label="Servicepartner">
                  Service&shy;partner
                </span>
              )}
          </div>
          {type === 'eki' && dealer.hasFMMData && (
            <ul className={cx('meta-nav')} {...dfmm}>
              {(isMobileNavigationNeeded
                ? [itemPhone, itemEmailAndWhatsApp, itemDealerWebsite, itemLocationSwitch]
                : [itemLocationSwitch, itemDealerWebsite, itemPhone, itemEmailAndWhatsApp]
              ).map((item, itemIndex) => (
                <Fragment key={itemIndex}>{item}</Fragment>
              ))}
            </ul>
          )}
          {type === 'hi' && (
            <ul className={cx('meta-nav')} {...dfmm}>
              <li className={cx('meta-nav__item')} {...dfmm}>
                <Link
                  to="https://portal.mazdaeur.com/mazdapedia/mazda-haendlerwebseite.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  label="FAQs"
                />
              </li>
              <li className={cx('meta-nav__item', 'meta-nav__item--border')} {...dfmm}>
                <Link to="/" label="Zur Webseite" />
              </li>
              <li className={cx('meta-nav__item', 'meta-nav__item--border')} {...dfmm}>
                <Link to="/admin/?logintype=logout" label="Logout" />
              </li>
            </ul>
          )}
        </div>
      </div>
    );
  };

  renderHeaderMainNavigation = (dfmm, dealer, type, forceMobileMenu) => (
    <div id="main-navigation" className={cx('header__main')}>
      <div className={cx('gr-container')} {...dfmm}>
        {type === 'eki' && (
          <div className={cx('logo-header')} {...dfmm}>
            <Link
              to="/"
              ariaLabel={`Zur Startseite von ${dealer.name}`}
              label={this.renderEkiLogo('logo-header', dealer.name)}
            />
          </div>
        )}
        <nav className={cx('main-nav')} aria-label="Hauptnavigation" {...dfmm}>
          <ul className={cx('main-nav__items')} {...dfmm}>
            {this.state.flyouts.map((entry) => {
              if (entry.hideInMenu === true) {
                return;
              }
              return (
                <MainEntry
                  id={`header-lvl-1-${entry.id}`}
                  ariaControls={`header-lvl-2-${entry.id}`}
                  key={`lvl-1-${entry.id}`}
                  entry={entry}
                  onClick={this.onMainEntryClick}
                  onKeyDown={this.onMainEntryKeyDown}
                  open={entry.open}
                  active={entry.active}
                  forceMobileMenu={forceMobileMenu}
                />
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );

  render() {
    const {
      dealer,
      error,
      forceMobileMenu,
      onOpenConfigurator,
      onOpenContact,
      setSharedState,
      setShowMapClicked,
      showMapClicked,
      teaser,
      type,
      updateMultipleUrlQuery,
    } = this.props;

    const {
      flyouts,
      flyoutSubOpen,
      isMobileNavigationNeeded,
      locationSwitchOpen,
      mapInit,
      mobileFlyoutHide,
      mobileFlyoutOpen,
    } = this.state;

    const dfmm = { 'data-force-mobile-menu': forceMobileMenu };

    const wrapperClass = cx('header__wrapper', {
      'header__wrapper--open': mobileFlyoutOpen,
      'header__wrapper--sub-opened': flyoutSubOpen,
    });

    const showLocationSwitch = dealer.hasFMMData && type === 'eki';
    const mobileBurgerMenuAriaLabel = 'Menü ' + (mobileFlyoutOpen ? 'verbergen' : 'anzeigen');

    const headerStyles = cx([styles.header], {
      'header--has-lead': true,
    });

    const headerMeta = this.renderHeaderMeta(
      isMobileNavigationNeeded,
      dfmm,
      dealer,
      type,
      locationSwitchOpen
    );
    const headerMainNavigation = this.renderHeaderMainNavigation(
      dfmm,
      dealer,
      type,
      forceMobileMenu
    );
    const headerLocationSwitch = showLocationSwitch ? (
      <LocationSwitch
        id={LOCATION_SWITCH_ID}
        dealer={dealer}
        open={locationSwitchOpen}
        init={mapInit}
        toggle={() => this.toggleLocationSwitch(LOCATION_SWITCH_ID)}
        showMapClicked={showMapClicked}
        setShowMapClicked={setShowMapClicked}
        forceOneCard={forceMobileMenu}
        setSharedState={setSharedState}
      />
    ) : undefined;

    return (
      <header
        id="header"
        title="Seitenbeginn"
        className={headerStyles}
        onKeyDown={this.onKeyDown}
        {...dfmm}
      >
        <div
          className={wrapperClass}
          hidden={
            !mobileFlyoutOpen && mobileFlyoutHide && isMobileNavigationNeeded ? true : undefined
          }
          id="header-bar"
          tabIndex={-1}
          {...dfmm}
        >
          <div className={cx('header__bar')} {...dfmm}>
            {type === 'eki' ? (
              <div className={cx('header__lead')} {...dfmm}>
                <div className={cx('gr-container')} {...dfmm}>
                  {!error && (
                    <LeadNav
                      dealer={dealer}
                      handleFlyout={this.handleFlyout}
                      onOpenContact={onOpenContact}
                      onOpenConfigurator={onOpenConfigurator}
                      updateMultipleUrlQuery={updateMultipleUrlQuery}
                      forceMobileMenu={forceMobileMenu}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className={cx('header__lead')} {...dfmm}>
                <div className={cx('gr-container')} {...dfmm}>
                  <LeadNav.Admin />
                </div>
              </div>
            )}
            {(isMobileNavigationNeeded
              ? [headerMainNavigation, headerMeta, headerLocationSwitch]
              : [headerMeta, headerLocationSwitch, headerMainNavigation]
            )
              .filter(Boolean)
              .map((item, itemIndex) => ({ ...item, key: itemIndex }))}
          </div>
          {flyouts.map((entry) => {
            if (
              entry.subentries &&
              (entry.hideInMenu === undefined || entry.hideInMenu === false)
            ) {
              return (
                <Flyout
                  id={`header-lvl-2-${entry.id}`}
                  key={`lvl-2-${entry.id}`}
                  open={entry.open}
                  design={entry.design}
                  entry={entry}
                  onLinkClick={this.onLinkClick}
                  onMainEntryClick={this.onMainEntryClick}
                  transitionDelay={entry.transitionDelay}
                  onOpenContact={onOpenContact}
                  onOpenConfigurator={onOpenConfigurator}
                  isServicePartnerOnly={dealer.isServicePartnerOnly()}
                  isSalesPartnerOnly={dealer.isSalesPartnerOnly()}
                  isOsbUser={dealer.settings.isOsbUser}
                  updateMultipleUrlQuery={updateMultipleUrlQuery}
                  forceMobileMenu={forceMobileMenu}
                  ariaLabelledby={`header-lvl-1-${entry.id}`}
                >
                  {teaser && this.renderTeaser(entry)}
                </Flyout>
              );
            }
            return '';
          })}
        </div>
        <div className={cx('header__mobile')}>
          <div className={cx('mobile-logo')}>
            <Link to="/" className={cx('mobile-logo__link')} onClick={this.onLogoClick}>
              <Logo className={cx('meta-logo__image')} width={44} height={39} {...dfmm} />
            </Link>
          </div>
          <div className={cx('mobile-dealer')}>
            {dealer.settings.servicePartnerOnly && (!dealer.hasLogo || !dealer.id) && (
              <span className={cx('mobile-dealer__service')} aria-label="Servicepartner">
                Servicepartner
              </span>
            )}

            <div className={cx('mobile-dealer__logo')}>
              {this.renderEkiLogo(
                'mobile-dealer__logo',
                type === 'eki' ? dealer.name : 'Händlerinterface'
              )}
            </div>
          </div>
          <button
            id="header-mobile-burger"
            className={cx(
              'mobile-burger',
              !mobileFlyoutHide && mobileFlyoutOpen && 'mobile-burger--open'
            )}
            aria-controls="header-bar"
            aria-label={mobileBurgerMenuAriaLabel}
            aria-expanded={mobileFlyoutOpen ? 'true' : 'false'}
            onClick={this.handleFlyout}
            {...dfmm}
          >
            <span className={cx('mobile-burger__icon')}>
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </header>
    );
  }
  // #endregion
}

export default withRouter(withUrlParams(Header));
